<template>
  <div class="actions-stack">
    <button
      type="button"
      class="vf-btn vf-btn--secondary"
      :class="{
        'vf-btn--done': captureState === 'done',
        'vf-btn--failed': captureState === 'failed',
      }"
      :disabled="captureDisabled || captureState === 'busy'"
      :title="captureTitle"
      @click="$emit('capture-evidence')"
    >
      <svg
        class="vf-btn__icon"
        viewBox="0 0 20 20"
        width="16"
        height="16"
        aria-hidden="true"
      >
        <!-- Camera body with viewfinder bump, aperture iris inside -->
        <path
          d="M6.2 4 L7.6 2.4 L12.4 2.4 L13.8 4 L16.6 4
             A1.6 1.6 0 0 1 18.2 5.6 L18.2 15.4
             A1.6 1.6 0 0 1 16.6 17 L3.4 17
             A1.6 1.6 0 0 1 1.8 15.4 L1.8 5.6
             A1.6 1.6 0 0 1 3.4 4 Z"
          fill="none"
          stroke="currentColor"
          stroke-width="1.3"
          stroke-linejoin="round"
        />
        <circle
          cx="10"
          cy="10.6"
          r="3.2"
          fill="none"
          stroke="currentColor"
          stroke-width="1.3"
        />
        <circle cx="10" cy="10.6" r="1" fill="currentColor" />
        <!-- Small indicator LED on top-right of body -->
        <circle cx="15.6" cy="6.4" r="0.7" fill="currentColor" />
      </svg>
      {{ captureLabel }}
    </button>
    <button
      type="button"
      class="vf-btn vf-btn--secondary"
      @click="$emit('complete-check')"
    >
      Complete Check
    </button>
    <button
      type="button"
      class="vf-btn vf-btn--primary"
      @click="$emit('generate-report')"
    >
      <svg
        class="vf-btn__icon"
        viewBox="0 0 20 20"
        width="16"
        height="16"
        aria-hidden="true"
      >
        <!-- Document with folded top-right corner and content lines -->
        <path
          d="M4 2.6 L13 2.6 L16.8 6.4 L16.8 17
             A0.8 0.8 0 0 1 16 17.8 L4 17.8
             A0.8 0.8 0 0 1 3.2 17 L3.2 3.4
             A0.8 0.8 0 0 1 4 2.6 Z"
          fill="none"
          stroke="currentColor"
          stroke-width="1.3"
          stroke-linejoin="round"
        />
        <!-- Folded corner triangle -->
        <path
          d="M13 2.6 L13 6.4 L16.8 6.4"
          fill="none"
          stroke="currentColor"
          stroke-width="1.3"
          stroke-linejoin="round"
        />
        <!-- Three content lines -->
        <line x1="5.8" y1="10.2" x2="14.2" y2="10.2" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" />
        <line x1="5.8" y1="12.8" x2="14.2" y2="12.8" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" />
        <line x1="5.8" y1="15.4" x2="11" y2="15.4" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" />
      </svg>
      {{ reportButtonLabel }}
    </button>
  </div>
</template>

<script>
/**
 * Right-panel bottom action stack.
 *
 * Three stacked buttons: capture evidence, complete check, generate report.
 * All emit events — parent (ResultsPanel) decides what to do. Phase 2
 * scaffolding stubs them to debug logs.
 *
 * Wireframe reference: AVA Verification Wireframe.html lines 515-529, 871-875.
 *
 * Class naming: `.vf-btn` prefix avoids collision with Vuetify's `.v-btn`
 * (different prefix namespace — `vf-*` = verification feature, `v-*` = Vuetify).
 *
 * Capture gating and its transient success state are PROPS, not local state:
 * this component cannot see the viewer or the store, and the parent already
 * owns the dispatch whose completion the "Captured" flash reports. Same shape
 * as `reportButtonLabel` — the label logic lives with the thing that knows.
 */
/**
 * A FAILED CAPTURE MUST SAY SO. Without this state a failure looks exactly like
 * a click that did nothing, and the surveyor walks away believing the support
 * is photographed. On an evidence workflow that is the worst available failure
 * mode — worse than an error dialog.
 */
const CAPTURE_LABELS = Object.freeze({
  idle: 'Capture Evidence',
  busy: 'Capturing…',
  done: 'Captured ✓',
  failed: 'Capture failed — retry',
});

export default {
  name: 'ActionsStack',
  props: {
    reportButtonLabel: {
      type: String,
      default: 'Generate Report',
    },
    captureDisabled: {
      type: Boolean,
      default: false,
    },
    /** 'idle' | 'busy' | 'done' — drives the label and the busy lock. */
    captureState: {
      type: String,
      default: 'idle',
    },
    /**
     * Why the button is disabled, in words. A greyed-out button with no
     * explanation is the most common complaint about gated actions — the
     * surveyor cannot tell "wait" from "broken".
     */
    captureTitle: {
      type: String,
      default: '',
    },
  },
  computed: {
    captureLabel() {
      return CAPTURE_LABELS[this.captureState] || CAPTURE_LABELS.idle;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.actions-stack {
  padding: 14px 16px;
  border-top: 1px solid $vf-line-soft;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #fbfbfb;
}

.vf-btn {
  height: 38px;
  border-radius: $vf-r-sm;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: $vf-font-body;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &__icon {
    display: block;
    flex-shrink: 0;
  }

  &--primary {
    background: $vf-primary;
    color: #fff;

    &:hover {
      background: #024a39;
      box-shadow: $vf-shadow-2;
      transform: translateY(-1px);
    }
  }

  &--secondary {
    background: #fff;
    color: $vf-ink;
    border-color: $vf-line-strong;

    &:hover {
      border-color: $vf-primary;
      color: $vf-primary;
    }
  }

  // Outcome flashes. Text and border only — no filled wash, matching the
  // feature's no-tinted-backgrounds rule.
  &--done {
    color: $vf-primary;
    border-color: $vf-primary;
  }

  &--failed {
    color: $vf-error;
    border-color: $vf-error;
  }

  // Every hover affordance is withdrawn, not just dimmed: a disabled button
  // that still lights up on hover reads as clickable-but-broken.
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;

    &:hover {
      box-shadow: none;
      transform: none;
    }
  }

  &--secondary:disabled:hover {
    border-color: $vf-line-strong;
    color: $vf-ink;
  }
}
</style>
